import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Chip,
  Box,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import {
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import {
  Search,
  RefreshCw,
  Eye,
  CheckCircle2,
  XCircle,
  CheckCheck,
  Package,
  FileText,
  DollarSign,
  Calendar,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { fetchFailedSyncs, fetchSyncStatistics, resolveSyncRecord } from './api';

// ---------------------------------------------------------------------
// Wired to the real backend (fetchFailedSyncs / fetchSyncStatistics /
// resolveSyncRecord) — only the stat cards / filter bar / table / footer
// layer was restyled to match the rest of the app. Status filtering and
// pagination both still go to the server (unlike ExecuteRequest.js,
// this page has real server-side pagination, so the 4 stat cards use
// fetchSyncStatistics's own totals rather than client-side counting —
// counting only the current page would be wrong). The Sync Record
// Details dialog is left as MUI, unchanged — same scope decision as the
// Complete Request dialog on ExecuteRequest.js.
// ---------------------------------------------------------------------

const STATUS_CARDS = [
  { key: 'all', label: 'Total Records', icon: Package, statKey: 'total' },
  { key: 'success', label: 'Successful', icon: CheckCircle2, statKey: 'success' },
  { key: 'failed', label: 'Failed', icon: XCircle, statKey: 'failed' },
  { key: 'resolved', label: 'Resolved', icon: CheckCheck, statKey: 'resolved' },
];

const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  success: {
    icon: (isDark) => (isDark ? 'text-green-400' : 'text-green-600'),
    active: (isDark) => (isDark ? 'border-green-500 bg-green-900/20' : 'border-green-500 bg-green-50'),
  },
  failed: {
    icon: (isDark) => (isDark ? 'text-red-400' : 'text-red-600'),
    active: (isDark) => (isDark ? 'border-red-500 bg-red-900/20' : 'border-red-500 bg-red-50'),
  },
  resolved: {
    icon: (isDark) => (isDark ? 'text-indigo-400' : 'text-indigo-600'),
    active: (isDark) => (isDark ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-500 bg-indigo-50'),
  },
};

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

const RECORD_TYPE_OPTIONS = [
  { value: 'allocation', label: 'Allocation' },
  { value: 'school_fees_allocation', label: 'School Fees Allocation' },
  { value: 'advance_loan', label: 'Advance Loan' },
  { value: 'school_fees_advance_loan', label: 'School Fees Advance' },
  { value: 'loan_application', label: 'Loan Application' },
];

const SyncDashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [syncRecords, setSyncRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    recordType: 'all',
    search: ''
  });
  const [dateFilter, setDateFilter] = useState('this_week');
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    resolved: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case 'today':
        return {
          start: today.toISOString().split('T')[0],
          end: today.toISOString().split('T')[0]
        };

      case 'this_week': {
        const firstDay = new Date(today);
        firstDay.setDate(today.getDate() - today.getDay());
        const lastDay = new Date(firstDay);
        lastDay.setDate(firstDay.getDate() + 6);
        return {
          start: firstDay.toISOString().split('T')[0],
          end: lastDay.toISOString().split('T')[0]
        };
      }

      case 'this_month': {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
          start: firstDay.toISOString().split('T')[0],
          end: lastDay.toISOString().split('T')[0]
        };
      }

      case 'custom':
        return customDateRange;

      default:
        return { start: '', end: '' };
    }
  };

  const loadSyncRecords = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filters.status !== 'all') params.status = filters.status;
      if (filters.recordType !== 'all') params.record_type = filters.recordType;
      if (filters.search) params.search = filters.search;
      if (dateRange.start) params.date_from = dateRange.start;
      if (dateRange.end) params.date_to = dateRange.end;

      const data = await fetchFailedSyncs(params);

      if (data.success) {
        setSyncRecords(data.failed_syncs || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          totalPages: data.pagination?.total_pages || 0
        }));
      }
    } catch (error) {
      console.error('Error loading sync records:', error);
      alert('Failed to load sync records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const dateRange = getDateRange();
      const params = {};

      if (dateRange.start) params.date_from = dateRange.start;
      if (dateRange.end) params.date_to = dateRange.end;

      const data = await fetchSyncStatistics(params);

      if (data.success && data.statistics) {
        setStats({
          total: data.statistics.total_records || 0,
          success: data.statistics.successful_syncs || 0,
          failed: data.statistics.failed_syncs || 0,
          resolved: data.statistics.resolved_syncs || 0
        });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleResolve = async (syncId) => {
    const notes = prompt('Enter resolution notes:');
    if (!notes) return;

    try {
      const data = await resolveSyncRecord(syncId, { resolution_notes: notes });

      if (data.success) {
        alert('Sync record resolved successfully!');
        loadSyncRecords();
        loadStatistics();
        setShowDetails(false);
      }
    } catch (error) {
      console.error('Error resolving sync:', error);
      alert('Failed to resolve sync record. Please try again.');
    }
  };

  useEffect(() => {
    loadSyncRecords();
    loadStatistics();
  }, [filters, dateFilter, customDateRange, pagination.page]);

  const getStatusBadgeClass = (status) => {
    if (status === 'success') return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
    if (status === 'failed') return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800';
    if (status === 'resolved') return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
    return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
  };

  const getRecordTypeMeta = (type) => {
    if (type.includes('school_fees')) return { color: '#2196f3', Icon: FileText };
    if (type.includes('loan')) return { color: '#9c27b0', Icon: DollarSign };
    return { color: isDark ? '#9aa4b2' : '#757575', Icon: Package };
  };

  const formatRecordTypeLabel = (type) =>
    type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  // Kept as MUI, unchanged in logic — the same scope decision as the
  // Complete Request dialog on ExecuteRequest.js.
  const DetailModal = ({ record, onClose }) => {
    if (!record) return null;

    const totalRequested = Object.values(record.request_data || {}).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
    const cashTotal = record.cash_collection?.found ? record.cash_collection.total_amount : 0;
    const difference = cashTotal - totalRequested;

    const statusChipColors = {
      success: { bg: '#4caf50', text: '#ffffff' },
      failed: { bg: '#f44336', text: '#ffffff' },
      resolved: { bg: '#3f51b5', text: '#ffffff' },
    };
    const statusChipColor = statusChipColors[record.sync_status] || { bg: theme.palette.grey[500], text: '#ffffff' };
    const typeMeta = getRecordTypeMeta(record.record_type);

    return (
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight="bold">Sync Record Details</Typography>
          <Typography variant="body2" color="text.secondary">{record.client_record_id}</Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: isDark ? '#1f262e' : '#f5f7fa' }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={record.sync_status.toUpperCase()}
                    size="small"
                    sx={{ backgroundColor: statusChipColor.bg, color: statusChipColor.text, fontWeight: 600 }}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: isDark ? '#1f262e' : '#f5f7fa' }}>
                <Typography variant="caption" color="text.secondary">Record Type</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={formatRecordTypeLabel(record.record_type)}
                    size="small"
                    sx={{
                      backgroundColor: isDark ? `${typeMeta.color}20` : `${typeMeta.color}15`,
                      color: typeMeta.color,
                      border: `1px solid ${typeMeta.color}40`,
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, backgroundColor: isDark ? '#1f262e' : '#f5f7fa' }}>
                <Typography variant="caption" color="text.secondary">Sync Attempts</Typography>
                <Typography variant="h6" fontWeight="bold">{record.sync_attempts}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {record.member_info && (
            <Paper sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                <Typography variant="subtitle1" fontWeight="bold">Member Information</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" fontWeight="500">{record.member_info.name}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Member ID</Typography>
                  <Typography variant="body2" fontWeight="500">{record.member_info.member_id}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" fontWeight="500">{record.member_info.phone}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {record.sync_status === 'failed' && record.error_message && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: isDark ? '#5d2a2a' : '#ffebee', border: '1px solid #f44336' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#f44336' }} />
                <Typography variant="subtitle1" fontWeight="bold" color="#f44336">Error Details</Typography>
              </Box>
              <Typography variant="body2" color="#f44336">{record.error_message}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <CalendarIcon sx={{ fontSize: 14, color: '#f44336' }} />
                <Typography variant="caption" color="#f44336">
                  Last attempt: {new Date(record.last_sync_attempt).toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          )}

          {record.request_data && (
            <Paper sx={{ p: 2, mb: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DescriptionIcon sx={{ color: theme.palette.text.secondary }} />
                <Typography variant="subtitle1" fontWeight="bold">Requested Allocation</Typography>
              </Box>
              {Object.entries(record.request_data).map(([key, value]) => (
                value > 0 && (
                  <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">KSh {value.toLocaleString()}</Typography>
                  </Box>
                )
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, mt: 1 }}>
                <Typography variant="body1" fontWeight="bold">Total Requested:</Typography>
                <Typography variant="body1" fontWeight="bold">KSh {totalRequested.toLocaleString()}</Typography>
              </Box>
            </Paper>
          )}

          {record.cash_collection?.found ? (
            <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MoneyIcon sx={{ color: '#4caf50' }} />
                <Typography variant="subtitle1" fontWeight="bold">Cash Collection Statement</Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: isDark ? '#1b3a1b' : '#e8f5e9' }}>
                    <Typography variant="caption" color="text.secondary">Cash Amount</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#4caf50">
                      KSh {record.cash_collection.cash_amount.toLocaleString()}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: isDark ? '#1a2a3a' : '#e3f2fd' }}>
                    <Typography variant="caption" color="text.secondary">M-Pesa Amount</Typography>
                    <Typography variant="h6" fontWeight="bold" color="#2196f3">
                      KSh {record.cash_collection.mpesa_amount.toLocaleString()}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 2, mb: 2, backgroundColor: isDark ? '#2a1a3a' : '#f3e5f5' }}>
                <Typography variant="caption" color="text.secondary">Total Collected</Typography>
                <Typography variant="h5" fontWeight="bold" color="#9c27b0">
                  KSh {record.cash_collection.total_amount.toLocaleString()}
                </Typography>
              </Paper>

              <Paper
                sx={{
                  p: 2,
                  backgroundColor: Math.abs(difference) < 0.01
                    ? (isDark ? '#1b3a1b' : '#e8f5e9')
                    : (isDark ? '#3a2a1a' : '#fff3e0'),
                  border: `1px solid ${Math.abs(difference) < 0.01 ? '#4caf50' : '#ff9800'}`
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight="600">
                    {Math.abs(difference) < 0.01 ? '✓ Amounts Match' : '⚠ Reconciliation Needed'}
                  </Typography>
                  {Math.abs(difference) >= 0.01 && (
                    <Typography variant="body2" fontWeight="bold" color="#ff9800">
                      Difference: KSh {Math.abs(difference).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
                <CalendarIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                <Typography variant="caption" color="text.secondary">
                  Collected: {new Date(record.cash_collection.collection_date).toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: 2, backgroundColor: isDark ? '#3a2a1a' : '#fff3e0', border: '1px solid #ff9800' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#ff9800' }} />
                <Typography variant="subtitle1" fontWeight="bold" color="#ff9800">No Cash Collection Found</Typography>
              </Box>
              <Typography variant="body2" color="#ff9800">
                No cash collection statement exists for this allocation.
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 2 }}>
          {record.sync_status === 'failed' && (
            <Button
              onClick={() => handleResolve(record.id)}
              variant="contained"
              color="success"
              sx={{ mr: 1 }}
            >
              Mark as Resolved
            </Button>
          )}
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div className="pb-32">
      {/* Status cards - Total / Successful / Failed / Resolved. Counts come
         from fetchSyncStatistics, which is independent of pagination and
         of the active status filter, so they always show the true totals
         for the selected date range. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {STATUS_CARDS.map((card) => {
          const Icon = card.icon;
          const isActive = filters.status === card.key;
          const accent = CARD_ACCENT[card.key];
          return (
            <button
              key={card.key}
              onClick={() => setFilters({ ...filters, status: card.key })}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : isDark
                    ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {stats[card.statKey]}
              </div>
              {card.key === 'success' && stats.total > 0 && (
                <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {Math.round((stats.success / stats.total) * 100)}% success rate
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Main panel: date range, filters, table and footer/pagination all
         in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Sync Dashboard
            </h2>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Field records that failed to sync, and their resolution status.
            </p>
          </div>
          <button
            onClick={() => { loadSyncRecords(); loadStatistics(); }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Date range */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`flex items-center gap-1.5 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            <Calendar className="w-4 h-4" />
            Date Range
          </div>
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              onClick={() => { setDateFilter(preset.key); setPagination(prev => ({ ...prev, page: 1 })); }}
              className={`px-4 py-2 border rounded-lg transition-colors ${
                dateFilter === preset.key
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                  : isDark
                    ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {preset.label}
            </button>
          ))}

          {dateFilter === 'custom' && (
            <>
              <div className="flex items-center gap-2">
                <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Start</label>
                <input
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                  className={`px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-[#2b323c] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>End</label>
                <input
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  style={{ colorScheme: isDark ? 'dark' : 'light' }}
                  className={`px-3 py-2 border rounded-lg ${
                    isDark ? 'bg-[#2b323c] border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
            </>
          )}
        </div>

        {/* Filters */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search records..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          <select
            value={filters.recordType}
            onChange={(e) => setFilters({ ...filters, recordType: e.target.value })}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Types</option>
            {RECORD_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading sync records...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Record ID
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Member
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Type
                  </th>
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Cash
                  </th>
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Attempts
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Last Sync
                  </th>
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {syncRecords.map((record) => {
                  const typeMeta = getRecordTypeMeta(record.record_type);
                  const TypeIcon = typeMeta.Icon;
                  return (
                    <tr key={record.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {record.client_record_id}
                      </td>
                      <td className="px-6 py-4">
                        {record.member_info ? (
                          <>
                            <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                              {record.member_info.name}
                            </div>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                              {record.member_info.member_id}
                            </div>
                          </>
                        ) : (
                          <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: isDark ? `${typeMeta.color}20` : `${typeMeta.color}15`,
                            color: typeMeta.color,
                            border: `1px solid ${typeMeta.color}40`,
                          }}
                        >
                          <TypeIcon className="w-3 h-3" />
                          {formatRecordTypeLabel(record.record_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeClass(record.sync_status)}`}>
                          {record.sync_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {record.cash_collection?.found ? (
                          <>
                            <div className={`font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                              KSh {record.cash_collection.total_amount.toLocaleString()}
                            </div>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                              {record.cash_collection.is_balanced ? '✓ Balanced' : '⚠ Unbalanced'}
                            </div>
                          </>
                        ) : (
                          <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No cash</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[1.75rem] px-2 py-1 rounded-full text-xs font-semibold ${
                          isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {record.sync_attempts}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                          {new Date(record.last_sync_attempt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => { setSelectedRecord(record); setShowDetails(true); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && syncRecords.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No records found</p>
            <p className="text-sm mt-1">Try adjusting your filters or search query</p>
          </div>
        )}

        {/* Footer / pagination */}
        <div className={`flex items-center justify-between gap-3 px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          <span>
            {pagination.totalPages > 1
              ? `Showing page ${pagination.page} of ${pagination.totalPages} (${pagination.total} total records)`
              : `Showing ${syncRecords.length} of ${pagination.total || syncRecords.length} records`}
          </span>
          {pagination.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className={`px-3 py-1.5 border rounded-lg font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className={`px-3 py-1.5 border rounded-lg font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal — MUI, unchanged */}
      {showDetails && selectedRecord && (
        <DetailModal
          record={selectedRecord}
          onClose={() => {
            setShowDetails(false);
            setSelectedRecord(null);
          }}
        />
      )}
    </div>
  );
};

export default SyncDashboard;